import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api, { platformAPI } from '../../lib/api';
import { 
  Building2, Users, TrendingUp, BarChart3, 
  AlertCircle, CheckCircle, PauseCircle, LogOut, Brain,
  ChevronDown, ChevronUp, User, Shield, Mail, Calendar,
  UserCog, X, Trash2, AlertTriangle
} from 'lucide-react';

const PlatformDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState({});
  const [loadingUsers, setLoadingUsers] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearResult, setClearResult] = useState(null);

  useEffect(() => {
    if (user?.role !== 'platform_owner') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, orgsRes, analyticsRes] = await Promise.all([
        api.getPlatformStats(),
        api.getPlatformOrganizations(),
        platformAPI.getAnalytics()
      ]);
      setStats(statsRes.data);
      setOrganizations(orgsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching platform data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleStatusChange = async (orgId, newStatus) => {
    try {
      await api.updateOrganization(orgId, { status: newStatus });
      fetchData();
    } catch (error) {
      console.error('Error updating organization:', error);
    }
  };

  const handlePlanChange = async (orgId, newPlan) => {
    try {
      await api.updateOrganization(orgId, { plan: newPlan });
      fetchData();
    } catch (error) {
      console.error('Error updating plan:', error);
    }
  };

  const toggleOrgUsers = async (orgId) => {
    if (expandedOrg === orgId) {
      setExpandedOrg(null);
      return;
    }
    
    setExpandedOrg(orgId);
    
    if (!orgUsers[orgId]) {
      setLoadingUsers(prev => ({ ...prev, [orgId]: true }));
      try {
        const res = await api.getOrganizationUsers(orgId);
        setOrgUsers(prev => ({ ...prev, [orgId]: res.data }));
      } catch (error) {
        console.error('Error fetching org users:', error);
      } finally {
        setLoadingUsers(prev => ({ ...prev, [orgId]: false }));
      }
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.updateUserRole(userId, newRole);
      // Refresh the users for the expanded org
      if (expandedOrg) {
        const res = await api.getOrganizationUsers(expandedOrg);
        setOrgUsers(prev => ({ ...prev, [expandedOrg]: res.data }));
      }
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  };

  const handleClearAllData = async () => {
    setClearing(true);
    setClearResult(null);
    try {
      const response = await platformAPI.clearAllData();
      setClearResult(response.data);
      // Refresh data after clearing
      await fetchData();
      // Reset org users cache
      setOrgUsers({});
      setExpandedOrg(null);
    } catch (error) {
      console.error('Error clearing data:', error);
      setClearResult({ error: error.response?.data?.detail || 'Failed to clear data' });
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const planColors = {
    starter: 'bg-gray-100 text-gray-800',
    professional: 'bg-blue-100 text-blue-800',
    enterprise: 'bg-purple-100 text-purple-800'
  };

  const roleColors = {
    platform_owner: 'bg-purple-100 text-purple-800',
    admin: 'bg-red-100 text-red-800',
    hr: 'bg-blue-100 text-blue-800',
    employee: 'bg-green-100 text-green-800'
  };

  const statusIcons = {
    active: <CheckCircle className="w-4 h-4 text-green-500" />,
    inactive: <PauseCircle className="w-4 h-4 text-gray-500" />,
    suspended: <AlertCircle className="w-4 h-4 text-red-500" />
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Platform Control Center</h1>
                <p className="text-sm text-gray-500">Manage organizations, users, and platform analytics</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                Platform Owner
              </span>
              <button
                onClick={() => setShowClearModal(true)}
                className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
                data-testid="clear-data-btn"
              >
                <Trash2 className="w-4 h-4" />
                <span>Clear All Data</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Organizations</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.total_organizations || 0}</p>
                <p className="text-sm text-green-600 mt-1">
                  {stats?.active_organizations || 0} active
                </p>
              </div>
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.total_users || 0}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {stats?.total_employees || 0} employees
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {['overview', 'organizations', 'users', 'analytics'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm capitalize transition-colors ${
                    activeTab === tab
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Signups</h3>
                <div className="space-y-3">
                  {stats?.recent_signups?.map((org, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{org.name}</p>
                          <p className="text-sm text-gray-500">{org.industry}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${planColors[org.plan]}`}>
                          {org.plan}
                        </span>
                        <span className="text-sm text-gray-500">
                          {new Date(org.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'organizations' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">All Organizations</h3>
                </div>
                <div className="space-y-4">
                  {organizations.map((org) => (
                    <div key={org.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Organization Header */}
                      <div 
                        className="flex items-center justify-between p-4 bg-white hover:bg-gray-50 cursor-pointer"
                        onClick={() => toggleOrgUsers(org.id)}
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{org.name}</p>
                            <p className="text-sm text-gray-500">{org.industry} • {org.size}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${planColors[org.plan]}`}>
                            {org.plan}
                          </span>
                          <div className="flex items-center space-x-1 text-sm text-gray-500">
                            <Users className="w-4 h-4" />
                            <span>{org.employee_count || 0} users</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            {statusIcons[org.status]}
                            <span className="text-sm capitalize">{org.status}</span>
                          </div>
                          {expandedOrg === org.id ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                      
                      {/* Expanded Users List */}
                      {expandedOrg === org.id && (
                        <div className="border-t border-gray-200 bg-gray-50 p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-700 flex items-center space-x-2">
                              <Users className="w-4 h-4" />
                              <span>Users in {org.name}</span>
                            </h4>
                            <div className="flex items-center space-x-2">
                              <select
                                value={org.plan}
                                onChange={(e) => { e.stopPropagation(); handlePlanChange(org.id, e.target.value); }}
                                className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${planColors[org.plan]}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value="starter">Starter</option>
                                <option value="professional">Professional</option>
                                <option value="enterprise">Enterprise</option>
                              </select>
                              <select
                                value={org.status}
                                onChange={(e) => { e.stopPropagation(); handleStatusChange(org.id, e.target.value); }}
                                className="text-xs border border-gray-300 rounded px-2 py-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="suspended">Suspended</option>
                              </select>
                            </div>
                          </div>
                          
                          {loadingUsers[org.id] ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                            </div>
                          ) : orgUsers[org.id]?.users?.length > 0 ? (
                            <div className="space-y-2">
                              {orgUsers[org.id].users.map((u) => (
                                <div 
                                  key={u.id} 
                                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                      <User className="w-4 h-4 text-indigo-600" />
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900 text-sm">{u.name}</p>
                                      <p className="text-xs text-gray-500 flex items-center space-x-1">
                                        <Mail className="w-3 h-3" />
                                        <span>{u.email}</span>
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-3">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColors[u.role]}`}>
                                      {u.role.replace('_', ' ')}
                                    </span>
                                    <button
                                      onClick={() => setSelectedUser(u)}
                                      className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                                    >
                                      <UserCog className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                              <p className="text-sm">No users in this organization</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">All Platform Users</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {organizations.map((org) => (
                    <div key={org.id} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <Building2 className="w-4 h-4 text-indigo-600" />
                        <h4 className="font-medium text-gray-900 text-sm">{org.name}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${planColors[org.plan]}`}>
                          {org.plan}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1 text-gray-500 text-sm">
                        <Users className="w-4 h-4" />
                        <span>{org.employee_count || 0} users</span>
                      </div>
                      <button
                        onClick={() => { setActiveTab('organizations'); toggleOrgUsers(org.id); }}
                        className="mt-2 text-xs text-indigo-600 hover:text-indigo-800"
                      >
                        View users →
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'analytics' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform Analytics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <BarChart3 className="w-5 h-5 text-indigo-600" />
                      <h4 className="font-medium text-gray-900">Usage Metrics</h4>
                    </div>
                    <div className="space-y-3 mt-4">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Sentiment Analyses</span>
                        <span className="font-medium">{analytics?.usage_metrics?.sentiment_analyses || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Voice Analyses</span>
                        <span className="font-medium">{analytics?.usage_metrics?.voice_analyses || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Surveys Created</span>
                        <span className="font-medium">{analytics?.usage_metrics?.surveys_created || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Alerts Generated</span>
                        <span className="font-medium">{analytics?.usage_metrics?.alerts_generated || 0}</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      <h4 className="font-medium text-gray-900">Growth (Last 30 Days)</h4>
                    </div>
                    <div className="space-y-3 mt-4">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">New Organizations</span>
                        <span className="font-medium text-green-600">+{analytics?.growth_trends?.new_organizations_30d || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">New Users</span>
                        <span className="font-medium text-green-600">+{analytics?.growth_trends?.new_users_30d || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* User Role Edit Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit User Role</h3>
              <button 
                onClick={() => setSelectedUser(null)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{selectedUser.name}</p>
                  <p className="text-sm text-gray-500">{selectedUser.email}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  defaultValue={selectedUser.role}
                  onChange={(e) => handleRoleChange(selectedUser.id, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="employee">Employee</option>
                  <option value="hr">HR Manager</option>
                  <option value="admin">Admin</option>
                  <option value="platform_owner">Platform Owner</option>
                </select>
              </div>
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={() => setSelectedUser(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clear All Data Modal */}
      {showClearModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <span>Clear All Platform Data</span>
              </h3>
              <button 
                onClick={() => { setShowClearModal(false); setClearResult(null); }}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {!clearResult ? (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium mb-2">This action will permanently delete:</p>
                  <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                    <li>All organizations</li>
                    <li>All users (except Platform Owners)</li>
                    <li>All employee profiles</li>
                    <li>All mood entries & wellness data</li>
                    <li>All sentiment analyses</li>
                    <li>All surveys & responses</li>
                    <li>All alerts</li>
                    <li>All voice analyses</li>
                  </ul>
                </div>
                <p className="text-sm text-gray-600">
                  This action <strong>cannot be undone</strong>. Are you sure you want to proceed?
                </p>
                <div className="flex space-x-3 pt-2">
                  <button
                    onClick={() => { setShowClearModal(false); setClearResult(null); }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    disabled={clearing}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleClearAllData}
                    disabled={clearing}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center space-x-2"
                    data-testid="confirm-clear-btn"
                  >
                    {clearing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Clearing...</span>
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        <span>Clear All Data</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : clearResult.error ? (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">Error: {clearResult.error}</p>
                </div>
                <button
                  onClick={() => { setShowClearModal(false); setClearResult(null); }}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 font-medium mb-3 flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5" />
                    <span>All data cleared successfully!</span>
                  </p>
                  <div className="text-sm text-green-700 space-y-1">
                    <p>• {clearResult.deleted?.organizations || 0} organizations deleted</p>
                    <p>• {clearResult.deleted?.users || 0} users deleted</p>
                    <p>• {clearResult.deleted?.employees || 0} employees deleted</p>
                    <p>• {clearResult.deleted?.mood_entries || 0} mood entries deleted</p>
                    <p>• {clearResult.deleted?.sentiment_analyses || 0} sentiment analyses deleted</p>
                    <p>• {clearResult.deleted?.surveys || 0} surveys deleted</p>
                    <p>• {clearResult.deleted?.alerts || 0} alerts deleted</p>
                    <p>• {clearResult.deleted?.voice_analyses || 0} voice analyses deleted</p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowClearModal(false); setClearResult(null); }}
                  className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformDashboard;
